import { useEffect, useRef, useState } from 'react'

/**
 * A drawer's tab selection persists across in-place navigation to a different finding, so the
 * persisted selection can point at a tab that isn't shown for the new finding. Fall back to
 * `fallbackTab` whenever the selection isn't in `visibleTabs`, so the drawer never renders an
 * orphaned, hidden tab's content. Callers build `visibleTabs` from the same booleans that gate the
 * `Tabs.Trigger`s, keeping trigger visibility and fallback eligibility a single source of truth.
 */
export const resolveVisibleTab = <Tab extends string>({
  selectedTab,
  visibleTabs,
  fallbackTab,
}: {
  selectedTab: Tab
  visibleTabs: readonly Tab[]
  fallbackTab: Tab
}): Tab => (visibleTabs.includes(selectedTab) ? selectedTab : fallbackTab)

export const useDrawerContentDimensions = ({ isEnabled }: { isEnabled: boolean }) => {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const [contentDimensions, setContentDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    if (!isEnabled) {
      return
    }
    const updateDimensions = () => {
      if (drawerContentRef.current) {
        setContentDimensions({
          width: drawerContentRef.current.offsetWidth,
          height: drawerContentRef.current.offsetHeight,
        })
      }
    }
    updateDimensions()
    // eslint-disable-next-line no-restricted-properties -- already inside useEffect with proper cleanup
    window.addEventListener('resize', updateDimensions)
    // eslint-disable-next-line no-restricted-properties -- already inside useEffect with proper cleanup
    return () => window.removeEventListener('resize', updateDimensions)
  }, [isEnabled])

  return { drawerContentRef, contentDimensions }
}
